/**
 * Change-password hook (task 632).
 *
 * Passwords live in Keycloak, never in Innovate. This hook calls the
 * innovate-service proxy `POST /api/users/me/password`, which forwards to AMS:
 * AMS verifies the CURRENT password with a Keycloak direct-grant (password)
 * check, then sets the NEW one via the Keycloak admin credential reset. The
 * request body is never logged and nothing is stored app-side.
 *
 * Backend status: the innovate-service proxy and its AMS endpoint are NOT built
 * yet (task 632 backend steps are unchecked). The page gates the live call
 * behind `CHANGE_PASSWORD_BACKEND_READY` in `SecurityPage.tsx`; this hook is the
 * client half of the intended contract, ready to light up when the endpoint
 * ships.
 *
 * Intended contract
 * -----------------
 *   POST /api/users/me/password
 *   body: { currentPassword: string, newPassword: string }
 *   200:  { success: true }
 *   403 { code: 'invalid_current_password' } — current password is wrong. NB:
 *        must NOT be 401 — the app's global response interceptor treats every
 *        401 as session expiry (silent token refresh, then a login redirect),
 *        which would swallow this body. Use 403 with the code.
 *   422 { code: 'password_policy_violation', message } — Keycloak policy said no;
 *        `message` is the policy text and is shown against the New password field
 *   429 { code: 'rate_limited' } — too many attempts (credential-guessing guard)
 */

import { useMutation } from '@tanstack/react-query';
import api from '../services/api';

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export const changePasswordApi = {
  /** POST /api/users/me/password — verify current, set new. Body is never logged. */
  update: (input: ChangePasswordInput) =>
    api.post<{ success: boolean }>('/users/me/password', input),
};

export const useChangePassword = () =>
  useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      changePasswordApi.update(input).then((res) => res.data),
  });

/** The distinct failures the change-password endpoint reports, in the app's terms. */
export type ChangePasswordFailure =
  | { kind: 'invalid_current'; message: string }
  | { kind: 'policy'; message: string }
  | { kind: 'rate_limited'; message: string }
  | { kind: 'general'; message: string };

/**
 * Classifies a failed change-password mutation into a shape the form can place:
 * a wrong current password lands on the Current field, a policy violation on the
 * New field (carrying Keycloak's own message), and rate-limiting / anything else
 * become a form-level notice. The service layer enhances axios errors with
 * `.status` and `.response` (the response body); we read `code`/`message` off
 * the body, falling back to status when the body is thin.
 */
export function classifyChangePasswordError(error: unknown): ChangePasswordFailure {
  const err = error as {
    status?: number;
    message?: string;
    response?: { code?: string; error?: string; message?: string };
  };
  const body = err?.response ?? {};
  const code = body.code;
  const serverMessage = body.message || body.error;

  // Keyed on the code, never on 401: a 401 is intercepted app-wide as session
  // expiry, so the endpoint signals a wrong current password with 403 + code.
  if (code === 'invalid_current_password') {
    return {
      kind: 'invalid_current',
      message: serverMessage || 'That current password is incorrect.',
    };
  }
  if (code === 'password_policy_violation' || err?.status === 422) {
    return {
      kind: 'policy',
      message: serverMessage || "That password does not meet the sign-in service's requirements.",
    };
  }
  if (code === 'rate_limited' || err?.status === 429) {
    return {
      kind: 'rate_limited',
      message:
        serverMessage || 'Too many attempts. Please wait a moment before trying again.',
    };
  }
  return {
    kind: 'general',
    message: 'Your password could not be changed. Please try again.',
  };
}
